import { supabase } from '../lib/supabase';
import { loadStripe } from '@stripe/stripe-js';

const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

export const subscriptionService = {
  // Get user's subscription status
  async getSubscription(userId) {
    try {
      const { data, error } = await supabase?.from('subscriptions')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(1)?.single();

      if (error && error?.code !== 'PGRST116') throw error;
      
      return {
        data: data ? {
          id: data?.id,
          userId: data?.user_id,
          stripeCustomerId: data?.stripe_customer_id,
          stripeSubscriptionId: data?.stripe_subscription_id,
          stripePriceId: data?.stripe_price_id,
          status: data?.status,
          tier: data?.tier,
          currentPeriodStart: data?.current_period_start,
          currentPeriodEnd: data?.current_period_end,
          cancelAtPeriodEnd: data?.cancel_at_period_end,
          canceledAt: data?.canceled_at,
          trialStart: data?.trial_start,
          trialEnd: data?.trial_end,
          createdAt: data?.created_at,
          updatedAt: data?.updated_at
        } : null,
        error: null
      };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to load subscription' } };
    }
  },

  // Create Stripe checkout session for Pro upgrade
  async createCheckoutSession(userId, tier = 'pro') {
    try {
      const { data, error } = await supabase?.functions?.invoke('create-checkout-session', {
        body: { userId, tier }
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to create checkout session' } };
    }
  },

  // Create Stripe customer portal session
  async createPortalSession(customerId) {
    try {
      const { data, error } = await supabase?.functions?.invoke('create-portal-session', {
        body: { customerId }
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to create portal session' } };
    }
  },

  // Get payment transactions
  async getPaymentTransactions(userId) {
    try {
      const { data, error } = await supabase?.from('payment_transactions')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) throw error;

      return {
        data: data?.map(tx => ({
          id: tx?.id,
          userId: tx?.user_id,
          subscriptionId: tx?.subscription_id,
          stripePaymentIntentId: tx?.stripe_payment_intent_id,
          stripeInvoiceId: tx?.stripe_invoice_id,
          amount: tx?.amount,
          currency: tx?.currency,
          status: tx?.status,
          description: tx?.description,
          createdAt: tx?.created_at
        })) || [],
        error: null
      };
    } catch (error) {
      return { data: [], error: { message: error?.message || 'Failed to load transactions' } };
    }
  },

  // Check if user has active subscription
  async hasActiveSubscription(userId) {
    try {
      const { data } = await this.getSubscription(userId);
      return data?.status === 'active' || data?.status === 'trialing';
    } catch (error) {
      return false;
    }
  },

  // Format currency
  formatAmount(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    })?.format(amount);
  },

  // Get Stripe instance
  getStripe() {
    return stripePromise;
  }
};

export default subscriptionService;
